import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Hook genérico para cargar datos asíncronos en una vista.
 * `deps` controla cuándo re-disparar la carga (igual semántica que useEffect).
 * `fetcher` debe ser estable; si lo defines inline en el componente, envuélvelo
 * en useCallback o pásalo via deps explícitas.
 */
export interface ApiState<T> {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => Promise<void>
}

export function useApi<T> (
  fetcher: () => Promise<T>,
  deps: ReadonlyArray<unknown> = []
): ApiState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)

  const run = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await fetcher()
      if (mountedRef.current) setData(r)
    } catch (e: any) {
      if (mountedRef.current) setError(e?.message ?? 'Error de red')
    } finally {
      if (mountedRef.current) setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    mountedRef.current = true
    void run()
    return () => { mountedRef.current = false }
  }, [run])

  return { data, loading, error, reload: run }
}
